<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick, inject } from 'vue';
import { TOOLTIP_DEBOUNCE_DURATION } from '../helpers/constants';
import EmojiPicker from 'vue3-emoji-picker';

const props = defineProps({
  targetLeft: Boolean,
  text: {
    type: String,
    default: ''
  },
  theme: {
    type: String,
    default: 'primary',
    validator: (value) => {
      return ['primary', 'secondary'].includes(value);
    },
  },
  xPosition: {
    type: String,
    default: 'right',
    validator: (value) => {
      return ['right', 'left', 'center'].includes(value);
    },
  },
  initial: {
    type: String,
    default: 'bottom',
    validator: (value) => {
      return ['bottom', 'top', 'left', 'right'].includes(value);
    },
  },
  rect: {
    type: DOMRect,
    required: true
  },
  custom: Boolean,
  persistent: Boolean,
  distance: {
    type: Number,
    default: 4
  }
});
const emit = defineEmits(['close', 'select-emoji']);
defineOptions({ inheritAttrs: false });
const { xPosition, persistent, distance } = props;
// provide/inject to resolve prop-drilling
const initial = inject('initial', props.initial);

const tooltip = ref(null);
const isTooltipOpen = ref(false);
const tooltipPosition = ref(initial);
const animationClass = ref(`scale-in-${tooltipPosition.value}-${xPosition}`);
const animationTimeout = ref(null);

const removeTooltip = () => {
  if (isTooltipOpen.value) {
    animationClass.value = `scale-out-${tooltipPosition.value}-${xPosition}`;
  }
};

const handleTooltipAnimationEnd = () => {
  if (animationClass.value.startsWith('scale-out')) {
    isTooltipOpen.value = false;
    emit('close');
  }
};

const handleSelectEmoji = (emoji) => {
  emit('select-emoji', emoji);
};

const handleOutsideClick = (event) => {
  if (tooltip.value && !tooltip.value.contains(event.target)) {
    removeTooltip();
  }
};

watch(isTooltipOpen, (newVal) => {
  const { rect } = props;
  if (tooltip.value && rect && newVal) {
    const tooltipHeight = tooltip.value.offsetHeight;
    const tooltipWidth = tooltip.value.offsetWidth;
    tooltip.value.style.left = `${rect.right - tooltipWidth}px`;
    if (xPosition === 'left') {
      tooltip.value.style.left = `${rect.left}px`;
    } else if (xPosition === 'center') {
      tooltip.value.style.left = `${rect.left + (rect.width - tooltipWidth) / 2}px`;
    }
    // Insure the tooltip is visible within the viewport
    // When there is not enough space to the bottom/top, show the tooltip on the opposite side
    const topPosition = `${rect.top + window.scrollY - tooltipHeight - distance}px`;
    const bottomPosition = `${rect.bottom + window.scrollY + distance}px`;
    const bottomDistance = window.innerHeight - rect.bottom - tooltipHeight - distance;
    const topDistance = rect.top - tooltipHeight - distance;
    if (initial === 'bottom') {
      if (bottomDistance < 0 && topDistance > bottomDistance) {
        tooltipPosition.value = 'bottom';
        tooltip.value.style.top = topPosition;
      } else {
        tooltipPosition.value = 'top';
        tooltip.value.style.top = bottomPosition;
      }
    } else if (initial === 'top') {
      if (topDistance < 0 && topDistance < bottomDistance) {
        tooltipPosition.value = 'top';
        tooltip.value.style.top = bottomPosition;
      } else {
        tooltipPosition.value = 'bottom';
        tooltip.value.style.top = topPosition;
      }
    } else {
      const leftPosition = `${rect.left + window.scrollX - tooltipWidth - distance}px`;
      const rightPosition = `${rect.right + window.scrollX + distance}px`;
      const rightDistance = window.innerWidth - rect.right - tooltipWidth - distance;
      const leftDistance = rect.left - tooltipWidth - distance;
      tooltip.value.style.top = `${rect.top + window.scrollY + (rect.height - tooltipHeight) / 2}px`;
      if (initial === 'left') {
        if (leftDistance < 0 && leftDistance < rightDistance) {
          tooltipPosition.value = 'left';
          tooltip.value.style.left = rightPosition;
        } else {
          tooltipPosition.value = 'right';
          tooltip.value.style.left = leftPosition;
        }
      } else if (initial === 'right') {
        const leftDistance = rect.left - tooltipWidth - distance;
        if (rightDistance < 0 && leftDistance > rightDistance) {
          tooltipPosition.value = 'right';
          tooltip.value.style.left = leftPosition;
        } else {
          tooltipPosition.value = 'left';
          tooltip.value.style.left = rightPosition;
        }
      }
    }
    animationClass.value = `scale-in-${tooltipPosition.value}-${xPosition}`;
  }
}, {
  // Make sure the watcher is triggered after the tooltip is rendered
  // But not yet painted to avoid flickering
  flush: 'post'
});

watch(() => props.targetLeft, async (newVal) => {
  if (newVal) {
    if (!persistent) {
      animationTimeout.value = setTimeout(() => {
        animationClass.value = `scale-out-${tooltipPosition.value}-${xPosition}`;
      }, TOOLTIP_DEBOUNCE_DURATION);
    } else {
      animationClass.value = `scale-out-${tooltipPosition.value}-${xPosition}`;
    }
  } else {
    if (animationTimeout.value) {
      clearTimeout(animationTimeout.value);
      animationClass.value = `scale-in-${tooltipPosition.value}-${xPosition}`;
    }
  }
});

onMounted(async () => {
  isTooltipOpen.value = true;
  await nextTick();
  window.addEventListener('click', handleOutsideClick);
  tooltip.value.focus();
});

onUnmounted(() => {
  window.removeEventListener('click', handleOutsideClick);
});

defineExpose({
  tooltip,
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isTooltipOpen"
      ref="tooltip"
      tabindex="-1"
      role="tooltip"
      :class="`pge-tooltip ${animationClass}`"
      @keyup.esc="removeTooltip"
      @animationend="handleTooltipAnimationEnd"
    >
      <EmojiPicker native v-bind="$attrs" @select="handleSelectEmoji" />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.pge-tooltip {
  position: absolute;
  width: max-content;
  padding: 4px 8px;
  z-index: 9999;

  &:focus-within {
    outline: none;
  }
}

:deep(.v3-body-inner) {
  scroll-behavior: smooth;
  scrollbar-color: #393d3f rgba(0, 0, 0, 0.1);
  scrollbar-width: thin;
}

:deep(.v3-body-inner::-webkit-scrollbar) {
  width: 8px;
}

:deep(.v3-body-inner::-webkit-scrollbar-track) {
  background-color: transparent;
}

:deep(.v3-body-inner::-webkit-scrollbar-thumb) {
  display: none;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 5px;
}

:deep(.v3-body-inner:hover::-webkit-scrollbar-thumb) {
  display: block;
}

:deep(.v3-emoji-picker) {
  height: 320px;
  width: 280px;
  box-shadow: 0 2px 10px #0003;
  border-radius: 10px;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  text-align: left;
}

:deep(.v3-emoji-picker *) {
  box-sizing: border-box;
}

:deep(.v3-emoji-picker .v3-header) {
  padding: 15px 15px 13px;
  border-bottom: 1px solid;
}

:deep(.v3-emoji-picker .v3-header .v3-groups) {
  display: flex;
}

:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group) {
  flex-grow: 1;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  font-size: 23px;
  cursor: pointer;
  position: relative;
  display: block;
  opacity: 0.7;
  transition: 0.2s;
}

:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group.v3-is-hidden) {
  display: none;
}

:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group:first-child),
:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group:last-child) {
  flex-grow: 0;
}

:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group:hover) {
  opacity: 1;
}

:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group span) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.v3-emoji-picker .v3-header .v3-groups .v3-group span img) {
  display: block;
  width: 1em;
  height: auto;
}

:deep(.v3-emoji-picker .v3-spacing) {
  height: 11px;
}

:deep(.v3-emoji-picker .v3-search input) {
  width: 100%;
  display: block;
  height: 26px;
  padding: 0 10px;
  border: 1px solid;
  border-radius: 3px;
  font-size: 12px;
  transition: 0.2s;
}

:deep(.v3-emoji-picker .v3-search input:focus) {
  outline: none;
}

:deep(.v3-emoji-picker .v3-body) {
  padding: 0 0 15px 11px;
  min-height: 0;
  flex-grow: 1;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner) {
  flex-grow: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  padding-right: 11px;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group h5) {
  margin: 0;
  top: 0;
  padding: 7px 0 3px 4px;
  z-index: 2;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group h5.v3-sticky) {
  position: sticky;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis) {
  display: flex;
  font-size: 18px;
  flex-wrap: wrap;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button) {
  cursor: pointer;
  border: none;
  background: none;
  margin: 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-basis: 12.5%;
  max-width: 12.5%;
  flex-grow: 1;
  padding: 0;
  font-size: 22px;
  position: relative;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button:after) {
  content: "";
  width: 100%;
  padding-bottom: 100%;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button span) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button img) {
  max-width: 100%;
  padding: 4px;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button span),
:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button img) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner.is-mac .v3-emojis button) {
  font-family: "Apple Color Emoji", sans-serif;
}

:deep(.v3-emoji-picker .v3-footer) {
  font-size: 14px;
  border-top: 1px solid #dddddd;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone),
:deep(.v3-emoji-picker .v3-footer .v3-foot-left) {
  display: flex;
  align-items: center;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone img),
:deep(.v3-emoji-picker .v3-footer .v3-foot-left img) {
  width: 20px;
  display: block;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone > span:first-child),
:deep(.v3-emoji-picker .v3-footer .v3-foot-left > span:first-child) {
  margin-right: 6px;
}

:deep(.v3-emoji-picker .v3-footer .v3-foot-left > span.v3-text) {
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone) {
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone > span) {
  display: inline-flex;
  vertical-align: top;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-text) {
  font-size: 13px;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon) {
  display: inline-flex;
  height: 15px;
  width: 15px;
  vertical-align: middle;
  align-self: center;
  border: 2px solid rgba(0, 0, 0, 0.2);
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon.v3-tone-neutral) {
  background-color: #ffd225;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon.v3-tone-1f3fb) {
  background-color: #ffdfbd;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon.v3-tone-1f3fc) {
  background-color: #e9c197;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon.v3-tone-1f3fd) {
  background-color: #c88e62;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon.v3-tone-1f3fe) {
  background-color: #a86637;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .v3-icon.v3-tone-1f3ff) {
  background-color: #60463a;
}

:deep(.v3-emoji-picker .v3-footer .v3-tone .is-mac span) {
  font-family: "Apple Color Emoji", sans-serif;
}

:deep(.v3-skin-tones) {
  position: absolute;
  height: 100%;
  width: 60%;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 15px;
  opacity: 0;
  visibility: hidden;
  transition: 0.2s;
  border-radius: 0 0 10px 10px;
}

:deep(.v3-skin-tones.v3-is-open) {
  opacity: 1;
  visibility: visible;
}

:deep(.v3-skin-tones .v3-skin-tone) {
  display: inline-block;
  height: 15px;
  width: 25px;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: 0ms;
}

:deep(.v3-skin-tones .v3-skin-tone:hover) {
  transform: scale(1.1);
  transition: 0.2s;
}

:deep(.v3-skin-tones .v3-skin-tone-neutral) {
  color: #ffd225;
  background-color: #ffd225;
}

:deep(.v3-skin-tones .v3-skin-tone-1f3fb) {
  color: #ffdfbd;
  background-color: #ffdfbd;
}

:deep(.v3-skin-tones .v3-skin-tone-1f3fc) {
  color: #e9c197;
  background-color: #e9c197;
}

:deep(.v3-skin-tones .v3-skin-tone-1f3fd) {
  color: #c88e62;
  background-color: #c88e62;
}

:deep(.v3-skin-tones .v3-skin-tone-1f3fe) {
  color: #a86637;
  background-color: #a86637;
}

:deep(.v3-skin-tones .v3-skin-tone-1f3ff) {
  color: #60463a;
  background-color: #60463a;
}

:deep(.v3-input-emoji-picker *) {
  box-sizing: border-box;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root) {
  position: relative;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-emoji-picker-input),
:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-emoji-picker-textarea) {
  width: 100%;
  height: 40px;
  border: 1px solid #999;
  padding-left: 15px;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-emoji-picker-textarea) {
  min-height: 80px;
  resize: vertical;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-emoji-picker-textarea + .v3-input-picker-wrap .v3-input-picker-icon) {
  top: auto;
  bottom: 5px;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-input-picker-wrap .v3-input-picker-icon) {
  display: inline-flex;
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  border: none;
  background: none;
  padding: 0 5px;
  cursor: pointer;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-input-picker-wrap .v3-input-picker-icon img) {
  display: block;
  width: 1em;
  height: 1em;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-input-picker-wrap .v3-emoji-picker) {
  opacity: 0;
  visibility: hidden;
  transition: 0.2s;
}

:deep(.v3-input-emoji-picker .v3-input-picker-root .v3-input-picker-wrap.v3-picker-is-open .v3-emoji-picker) {
  opacity: 1;
  visibility: visible;
  z-index: 999;
}

:deep(.v3-emoji-picker) {
  --v3-picker-bg: #ffffff;
  --v3-picker-fg: #000000;
  --v3-picker-border: #dddddd;
  --v3-picker-input-bg: var(--v3-picker-bg);
  --v3-picker-input-border: #cccccc;
  --v3-picker-input-focus-border: #000000;
  --v3-group-image-filter: none;
  --v3-picker-emoji-hover: #f7f7f7;
  background: var(--v3-picker-bg);
  color: var(--v3-picker-fg);
}

:deep(.v3-emoji-picker .v3-footer),
:deep(.v3-emoji-picker .v3-header) {
  border-color: var(--v3-picker-border);
}

:deep(.v3-emoji-picker .v3-groups) {
  filter: var(--v3-group-image-filter);
}

:deep(.v3-emoji-picker .v3-tone) {
  color: var(--v3-picker-fg);
}

:deep(.v3-emoji-picker .v3-search input) {
  background: var(--v3-picker-input-bg);
  border-color: var(--v3-picker-input-border);
  color: inherit;
}

:deep(.v3-emoji-picker .v3-search input:focus) {
  border-color: var(--v3-picker-input-focus-border);
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group h5),
:deep(.v3-emoji-picker .v3-skin-tones) {
  background: var(--v3-picker-bg);
}

:deep(.v3-emoji-picker .v3-body .v3-body-inner .v3-group .v3-emojis button:hover) {
  background: var(--v3-picker-emoji-hover, #f7f7f7);
}

@media (prefers-color-scheme: dark) {
  :deep(.v3-emoji-picker.v3-color-theme-auto) {
    --v3-picker-bg: #000000;
    --v3-picker-fg: #ffffff;
    --v3-picker-border: #333333;
    --v3-picker-input-bg: #222222;
    --v3-picker-input-border: #444444;
    --v3-picker-input-focus-border: #555555;
    --v3-group-image-filter: invert(1);
    --v3-picker-emoji-hover: #222222;
  }
}

:deep(.v3-emoji-picker.v3-color-theme-dark) {
  --v3-picker-bg: #000000;
  --v3-picker-fg: #ffffff;
  --v3-picker-border: #333333;
  --v3-picker-input-bg: #222222;
  --v3-picker-input-border: #444444;
  --v3-picker-input-focus-border: #555555;
  --v3-group-image-filter: invert(1);
  --v3-picker-emoji-hover: #222222;
}
</style>

